import { useRef } from 'react';
import { useRouter } from 'next/router';
// Types
import { InputActionMeta } from 'react-select';
import { SearchResultType } from 'api/graphql-analytics-global-types';
// Constants
import { HOME, SEARCH } from 'constants/routes';
// Hooks
// Context
import { useSearchBarContext } from './hook/useSearchBarContext';
// Ui3
import Select from 'ui3/Select/Select';
import Button from 'ui3/Button/Button';
// Styles
import styles from './SearchBar.module.scss';

export type SearchBarOptionProps = {
  name: string;
  searchTerm: string;
  value: string;
  label: string;
  url: string;
  id: string;
  type: SearchResultType;
  // Rich display fields
  imageUrl?: string;
  sports?: string[];
  username?: string;
  price?: number | null;
  isFree?: boolean;
  storeName?: string;
  scheduleDate?: string;
  isVideo?: boolean;
};

export type ShopCategories = {
  merch: boolean;
  memorabilia: boolean;
  experiences: boolean;
};

export type ShopAllOptions = {
  merch: SearchBarOptionProps[];
  memorabilia: SearchBarOptionProps[];
  experiences: SearchBarOptionProps[];
};

export type SearchBarGroupProps = {
  label: string;
  options: SearchBarOptionProps[];
  // For Shop/Latest Drops section with tabs
  availableCategories?: ShopCategories;
  allOptions?: ShopAllOptions;
};

export type FilterOptionOption = {
  label: string;
  value: string;
  data: SearchBarOptionProps;
};

const SearchBar = () => {
  const searchBarWrapperRef = useRef<HTMLDivElement | null>(null);

  const { push, pathname } = useRouter();

  const {
    searchValue,
    setSearchValue,
    options,
    loading,
    setSearchFocused,
    setSelectedOption,
  } = useSearchBarContext();

  const isHomepage = pathname === HOME;

  const optionValue = searchValue
    ? {
        label: searchValue,
        value: searchValue,
      }
    : null;

  const onInputChange = (value: string, action: InputActionMeta) => {
    if (action.action !== 'input-blur' && action.action !== 'menu-close') {
      setSearchValue(value);

      return value;
    }

    if (action.action === 'input-blur') {
      setSearchValue(searchValue);

      return searchValue;
    }
  };

  const onSearchApply = (option: SearchBarOptionProps) => {
    // checking if option exists-in case of clear, it will be null, otherwise it will be an object
    if (option) {
      // send analytics when user selects an autocomplete option along with relevant data (search type and id)
      setSelectedOption(option);
    }
    setSearchValue(option.searchTerm);
    push(option.url);

    return option;
  };

  const navigateToSearchPage = () => {
    push(`${SEARCH}?q=${encodeURIComponent(searchValue)}`);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      // setting focused false will trigger the analytics api without the timer
      setSearchFocused(false);

      navigateToSearchPage();
    }
  };

  const commonSelectProps = {
    className: styles.searchBarSelect,
    placeholder: 'Search Athletes, Sports, Streams and more...',
    defaultOptions: true,
    getOptionLabel: (option: SearchBarOptionProps) => option.label,
    getOptionValue: (option: SearchBarOptionProps) => option.value,
    options,
    isLoading: loading,
    onInputChange,
    onChange: onSearchApply,
    onKeyDown,
    value: optionValue,
    defaultValue: optionValue,
    inputValue: searchValue,
    defaultInputValue: searchValue,
    cacheOptions: true,
    dropdownIndicator: null,
  } as const;

  if (!isHomepage) {
    return null;
  }

  return (
    <div className={styles.root} ref={searchBarWrapperRef}>
      <Select
        {...commonSelectProps}
        id="homepageSearch"
        inputId="homepageSearchInput"
        onFocus={() => setSearchFocused(true)}
        onBlur={() => setSearchFocused(false)}
        styles={{
          container: (provided) => ({
            ...provided,
            display: 'block',
          }),
          control: (provided, state) => ({
            ...provided,
            backgroundColor: '#FFFFFF',
            borderColor: state.isFocused ? '#CCCCCC' : '#CCCCCC',
            color: '#000000',
            borderRadius: 0,
            borderTopLeftRadius: '8px',
            borderBottomLeftRadius: '8px',
            '&:hover': {
              borderColor: '#999999',
            },
          }),
          input: (provided) => ({
            ...provided,
            lineHeight: '20px',
            fontSize: '18px',
            paddingTop: '10px',
            paddingBottom: '10px',
            borderRadius: 0,
            '@media only screen and (min-width: 768px)': {
              lineHeight: '30px',
              fontSize: '18px',
              paddingTop: '10px',
              paddingBottom: '10px',
              paddingLeft: '16px',
            },
          }),
          placeholder: (provided) => ({
            ...provided,
            textOverflow: 'ellipsis',
            width: '100%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            fontSize: '16px',
            borderRadius: 0,
            maxWidth: 'calc(360px - 106px - 8px)',
            '@media only screen and (min-width: 768px)': {
              maxWidth: 'calc(600px - 106px - 8px)',
              fontSize: '18px',
            },
            '@media only screen and (min-width: 991px)': {
              maxWidth: `calc(800px - 138px - 8px)`,
              fontSize: '18px',
              paddingLeft: '5px',
            },
          }),
          menu: (provided) => ({
            ...provided,
            width: searchBarWrapperRef.current?.offsetWidth,
            marginTop: 0,
            borderRadius: 0,
            borderTopLeftRadius: '8px',
            borderBottomLeftRadius: '8px',
            zIndex: 3,
            backgroundColor: '#FFFFFF',
            border: '1px solid #CCCCCC',
          }),
          option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#F0F0F0' : '#FFFFFF',
            color: '#000000',
            borderRadius: 0,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#F0F0F0',
            },
          }),
          singleValue: (provided) => ({
            ...provided,
            color: '#000000',
            borderRadius: 0,
          }),
          valueContainer: (provided) => ({
            ...provided,
            textOverflow: 'ellipsis',
            width: '100%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            display: 'initial',
            textAlign: 'left',
            color: '#000000',
            borderRadius: 0,
            maxWidth: 'calc(360px - 106px)',
            '@media only screen and (min-width: 768px)': {
              maxWidth: 'calc(600px - 106px)',
            },
            '@media only screen and (min-width: 991px)': {
              maxWidth: `calc(800px - 138px)`,
            },
          }),
          groupHeading: (provided) => ({
            ...provided,
            textAlign: 'left',
            fontSize: '16px',
            color: '#B6B6B6',
            borderRadius: 0,
          }),
        }}
      />

      <Button className={styles.buttonStyle} onClick={navigateToSearchPage}>
        <p className={styles.customButtonTextStyle}>Search</p>
      </Button>
    </div>
  );
};

export default SearchBar;
